import { useState } from "react";
import { toast } from "sonner";
import { FlaskConical, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { isPreviewHost } from "@/lib/selfheal";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { ms } from "@/components/health/HealthGauge";

export const PlaybookCard = ({ items, seed }) => {
  const { t } = useI18n();
  return (
    <section className="card-carbon min-w-0 rounded-2xl p-5" data-testid="health-playbook">
      <h2 className="font-display text-base font-bold text-white md:text-lg">{t("heal.playbook")}</h2>
      <p className="mt-1 text-xs text-neutral-400">{t("heal.playbookHint")}</p>
      {seed?.version && <p className="mt-1 text-xs text-neutral-500" data-testid="health-seed">{t("heal.seed", { v: seed.version, n: seed.entries })}</p>}
      <div className="mt-3 max-h-80 overflow-auto">
        <table className="w-full min-w-[30rem] text-start text-xs">
          <thead className="text-neutral-400"><tr><th className="p-1 text-start">{t("heal.signature")}</th><th className="p-1 text-start">{t("heal.best")}</th><th className="p-1 text-start">{t("heal.count")}</th><th className="p-1 text-start">{t("heal.avg")}</th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.signature} className="border-t border-white/5 text-neutral-200" data-testid="health-playbook-row">
                <td className="p-1"><bdi className="break-all">{p.signature}</bdi></td>
                <td className="p-1"><bdi>{(p.best || "—").split(":").pop()}</bdi></td>
                <td className="p-1">{p.count}</td>
                <td className="p-1"><bdi>{p.count ? ms(p.avg_recover_ms) : "—"}</bdi></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const pairOf = (results) => {
  const rows = new Map();
  results.forEach((r) => rows.set(r.fault, { ...rows.get(r.fault), [`p${r.pass}`]: r }));
  return [...rows.entries()];
};

export const TrainingCard = ({ training, results, onStarted }) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      await api.post("/admin/selfheal/test-hook", { fault: "train" });
      toast.success(t("heal.trainStarted"));
      onStarted();
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card-carbon min-w-0 rounded-2xl p-5" data-testid="health-training">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="min-w-0 font-display text-base font-bold text-white md:text-lg">{t("heal.training")}</h2>
        {isPreviewHost() && (
          <button type="button" className="btn-gold btn-sm" onClick={start} disabled={busy || training?.running} data-testid="health-train-btn">
            {busy || training?.running ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />} {t("heal.trainStart")}
          </button>
        )}
      </div>
      {training?.running && <p className="mt-2 text-xs text-amber-200" data-testid="health-train-progress">{t("heal.trainRunning", { p: training.pass, s: training.step })}</p>}
      {results.length > 0 && (
        <div className="mt-3 max-h-[32rem] overflow-auto">
          <table className="w-full min-w-[40rem] text-xs" data-testid="health-training-table">
            <thead className="text-neutral-400"><tr>{["fault", "drop", "detected", "repair", "pass1", "pass2"].map((k) => <th key={k} className="p-1 text-start">{t(`heal.${k}`)}</th>)}</tr></thead>
            <tbody>
              {pairOf(results).map(([fault, { p1, p2 }]) => (
                <tr key={fault} className="border-t border-white/5 text-neutral-200" data-testid="health-training-row">
                  <td className="p-1"><bdi>{fault}</bdi></td>
                  <td className="p-1"><bdi>{p1?.gauge_drop != null ? `${p1.gauge_before.toFixed(4)} → ${p1.gauge_drop.toFixed(4)}` : "—"}</bdi></td>
                  <td className="whitespace-nowrap p-1"><bdi>{p1?.detected || "—"}</bdi></td>
                  <td className="p-1"><bdi>{(p2?.repair || p1?.repair || "—").split(":").pop()}</bdi></td>
                  {[p1, p2].map((p, i) => (
                    <td key={i} className={`p-1 ${p && !p.ok ? "text-red-300" : ""}`}><bdi>{p ? `${ms(p.detect_ms)} + ${ms(p.repair_ms)} (${p.tries})` : "—"}</bdi></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
